import React, { useState } from 'react'
import {
  ClipboardDocumentIcon,
  GlobeAltIcon,
  SparklesIcon,
  ExclamationTriangleIcon,
  MagnifyingGlassIcon
} from '@heroicons/react/24/outline'
import urlInfoService from '../utils/urlInfoService'

const SmartBookmarkHelper = ({ onFillData, className = '' }) => {
  const [isLoading, setIsLoading] = useState(false)
  const [error, setError] = useState('')
  const [searchQuery, setSearchQuery] = useState('')
  const [searchResults, setSearchResults] = useState([])

  // 从剪贴板读取URL并自动填充
  const handlePasteFromClipboard = async () => {
    setIsLoading(true)
    setError('')

    try {
      const urlInfo = await urlInfoService.getFromClipboard()

      // 填充数据
      onFillData({
        abbr: urlInfo.abbr,
        href: urlInfo.href || urlInfo.domain,
        title: urlInfo.title,
        icon: urlInfo.icon
      })

    } catch (err) {
      setError(err.message)
    } finally {
      setIsLoading(false)
    }
  }

  // 手动输入URL并获取信息
  const handleManualUrl = async () => {
    const url = prompt('请输入网页URL:\n\n💡 小贴士：\n• 支持不完整URL（如：github.com）\n• 自动添加https://协议\n• 智能识别常见网站')
    if (!url) return

    setIsLoading(true)
    setError('')

    try {
      const urlInfo = await urlInfoService.getUrlInfo(url.trim())

      onFillData({
        abbr: urlInfo.abbr,
        href: url.trim(),
        title: urlInfo.title,
        icon: urlInfo.icon
      })

    } catch (err) {
      setError(err.message)
    } finally {
      setIsLoading(false)
    }
  }

  // 批量添加URL
  const handleBatchUrl = async () => {
    const urls = prompt('批量添加URL（每行一个）:\n\n💡 支持：\n• 多个URL用换行分隔\n• 自动过滤无效URL\n• 智能去重')
    if (!urls) return

    setIsLoading(true)
    setError('')

    try {
      const urlList = urls.split('\n')
        .map(url => url.trim())
        .filter(url => url.length > 0)

      if (urlList.length === 0) {
        throw new Error('请输入至少一个有效的URL')
      }

      // 处理第一个URL（当前只支持单个）
      const urlInfo = await urlInfoService.getUrlInfo(urlList[0])

      onFillData({
        abbr: urlInfo.abbr,
        href: urlList[0],
        title: urlInfo.title,
        icon: urlInfo.icon
      })

      if (urlList.length > 1) {
        setError(`已添加第一个URL，其余${urlList.length - 1}个URL请逐个添加`)
      }

    } catch (err) {
      setError(err.message)
    } finally {
      setIsLoading(false)
    }
  }

  // 搜索网站
  const handleSearch = (query) => {
    setSearchQuery(query)
    if (query.trim()) {
      const results = urlInfoService.searchSites(query)
      setSearchResults(results)
    } else {
      setSearchResults([])
    }
  }

  // 智能建议常用网站
  const handleQuickAdd = (site) => {
    onFillData(site)
  }

  const quickSites = urlInfoService.getPopularSites()

  return (
    <div className={`space-y-4 ${className}`}>
      {/* 功能说明 */}
      <div className="bg-blue-900/20 border border-blue-700/50 rounded-lg p-3">
        <div className="flex items-start">
          <div className="flex-shrink-0">
            <SparklesIcon className="w-5 h-5 text-blue-400 mt-0.5" />
          </div>
          <div className="ml-3">
            <h4 className="text-sm font-medium text-blue-300">智能书签助手</h4>
            <div className="mt-1 text-xs text-blue-200">
              <p>• 从剪贴板快速添加：复制网址后点击"从剪贴板"</p>
              <p>• 搜索常见网站：输入网站名称快速查找</p>
              <p>• 手动输入网址：支持任意网站自动解析</p>
            </div>
          </div>
        </div>
      </div>

      {/* 智能填充按钮 */}
      <div className="flex flex-wrap gap-2">
        <button
          type="button"
          onClick={handlePasteFromClipboard}
          disabled={isLoading}
          className="btn-secondary btn-sm flex items-center"
          title="从剪贴板粘贴URL并自动填充信息"
        >
          <ClipboardDocumentIcon className="w-4 h-4 mr-1" />
          {isLoading ? '获取中...' : '从剪贴板'}
        </button>

        <button
          type="button"
          onClick={handleManualUrl}
          disabled={isLoading}
          className="btn-secondary btn-sm flex items-center"
          title="手动输入URL并获取信息"
        >
          <GlobeAltIcon className="w-4 h-4 mr-1" />
          手动输入
        </button>

        <button
          type="button"
          onClick={handleBatchUrl}
          disabled={isLoading}
          className="btn-secondary btn-sm flex items-center"
          title="批量输入多个URL"
        >
          <GlobeAltIcon className="w-4 h-4 mr-1" />
          批量添加
        </button>
      </div>

      {/* 搜索框 */}
      <div className="relative">
        <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
          <MagnifyingGlassIcon className="h-4 w-4 text-gray-400" />
        </div>
        <input
          type="text"
          value={searchQuery}
          onChange={(e) => handleSearch(e.target.value)}
          placeholder="搜索网站..."
          className="input pl-10"
        />
      </div>

      {/* 搜索结果 */}
      {searchResults.length > 0 && (
        <div className="space-y-2">
          <div className="text-sm text-gray-400">搜索结果</div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-2 max-h-40 overflow-y-auto">
            {searchResults.map((site) => (
              <button
                key={site.domain}
                type="button"
                onClick={() => handleQuickAdd(site)}
                className="p-2 text-left bg-gray-700/30 hover:bg-gray-700/50 rounded-lg transition-colors text-sm"
                title={`添加 ${site.title}`}
              >
                <div className="font-medium text-white">{site.abbr}</div>
                <div className="text-xs text-gray-400 truncate">{site.title}</div>
              </button>
            ))}
          </div>
        </div>
      )}

      {/* 浏览器限制说明 */}
      <div className="bg-amber-900/20 border border-amber-700/50 rounded-lg p-3">
        <div className="flex items-start">
          <ExclamationTriangleIcon className="w-5 h-5 text-amber-400 mt-0.5 flex-shrink-0" />
          <div className="ml-3">
            <h4 className="text-sm font-medium text-amber-300">为什么无法直接读取浏览器标签页？</h4>
            <div className="mt-1 text-xs text-amber-200">
              <p>• <strong>安全限制</strong>：浏览器禁止网页访问其他标签页信息以保护隐私</p>
              <p>• <strong>同源策略</strong>：Web应用只能访问同源内容，无法跨域获取页面信息</p>
              <p>• <strong>解决方案</strong>：需要开发浏览器扩展才能实现此功能</p>
            </div>
            <div className="mt-2">
              <a
                href="/docs/browser-extension-solution.md"
                target="_blank"
                className="text-xs text-amber-300 hover:text-amber-200 underline"
              >
                📖 查看浏览器扩展解决方案
              </a>
            </div>
          </div>
        </div>
      </div>

      {/* 错误提示 */}
      {error && (
        <div className="flex items-center p-3 bg-red-900/20 border border-red-700/50 rounded-lg">
          <ExclamationTriangleIcon className="w-5 h-5 text-red-400 mr-2 flex-shrink-0" />
          <span className="text-sm text-red-300">{error}</span>
        </div>
      )}

      {/* 快速添加常用网站 */}
      {searchQuery === '' && (
        <div className="space-y-2">
          <div className="flex items-center text-sm text-gray-400">
            <SparklesIcon className="w-4 h-4 mr-1" />
            快速添加常用网站
          </div>
          <div className="grid grid-cols-2 md:grid-cols-3 gap-2">
            {quickSites.map((site) => (
              <button
                key={site.abbr}
                type="button"
                onClick={() => handleQuickAdd(site)}
                className="p-2 text-left bg-gray-700/30 hover:bg-gray-700/50 rounded-lg transition-colors text-sm"
                title={`添加 ${site.title}`}
              >
                <div className="font-medium text-white">{site.abbr}</div>
                <div className="text-xs text-gray-400 truncate">{site.title}</div>
              </button>
            ))}
          </div>
        </div>
      )}
    </div>
  )
}

export default SmartBookmarkHelper
